import { ChainId } from '@src/configs/chainInfo'
import { ResultCode, StepNames } from '@src/enums'
import { useJupiterSwap } from '@src/hooks/useJupiterSwap'
import { IJupiterQuoteResponse } from '@src/types/jupiter'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const logger = createContextLogger('useJupiterSwapIntegration')

/**
 * Hook to integrate Jupiter swap functionality into the exchange flow
 */
export function useJupiterSwapIntegration() {
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const { setWaitingForConnectedWalletInteraction } = useApplicationStore()
    const { executeJupiterSwap } = useJupiterSwap()

    /**
     * Execute a Jupiter swap
     * @param quoteData Jupiter quote response
     * @param exchangeId Exchange ID for history tracking
     * @returns Transaction signature or null if failed
     */
    const executeSwap = async (quoteData: IJupiterQuoteResponse, exchangeId: string): Promise<string | null> => {
        try {
            logger.info('Executing swap', { exchangeId })

            // Log detailed information about the Jupiter quote data for debugging
            logger.info('Quote data in integration layer', {
                hasQuoteData: !!quoteData,
                quoteType: typeof quoteData,
                quoteKeys: quoteData ? Object.keys(quoteData) : [],
                hasTxData: quoteData && !!quoteData.tx_data,
                txDataKeys: quoteData && quoteData.tx_data ? Object.keys(quoteData.tx_data) : [],
                hasJupiterQuoteProperty: quoteData && !!quoteData.jupiter_quote,
                jupiterQuotePropertyType:
                    quoteData && quoteData.jupiter_quote ? typeof quoteData.jupiter_quote : 'undefined',
            })

            // Update history to show we're confirming the swap
            updateHistory(exchangeId, {
                currentStep: StepNames.CONFIRM_SWAP,
                result: {
                    status: ResultCode.pending,
                    message: 'Please confirm swap.',
                },
            })

            // Update history to show we're waiting for confirmation
            updateHistory(exchangeId, {
                currentStep: StepNames.CONFIRM_SWAP_PENDING,
                result: {
                    status: ResultCode.pending,
                    message: 'Waiting for swap confirmation.',
                },
            })

            // Execute the swap
            const signature = await executeJupiterSwap(quoteData)

            if (!signature) {
                throw new Error('Jupiter swap failed')
            }

            // Update history with successful swap
            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_EXECUTED,
                result: {
                    status: ResultCode.success,
                    message: 'Swap completed successfully.',
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(ChainId.Solana)}/${signature}`,
                },
            })

            return signature
        } catch (error: any) {
            if (error.message?.includes('User rejected') || error.message?.includes('user rejected')) {
                logger.info('User rejected the swap transaction')
                return null
            }

            logger.error('Swap failed:', error)

            // Update history with failure
            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_TIMED_OUT,
                result: {
                    status: ResultCode.failure,
                    message: error instanceof Error ? error.message : 'Unknown error during Jupiter swap',
                },
            })

            throw error
        } finally {
            setWaitingForConnectedWalletInteraction(false)
        }
    }

    return {
        executeJupiterSwap: executeSwap,
    }
}
